import { useTranslation } from 'react-i18next';
import { InvoiceDocuments, InvoiceItemsTable } from './components';
import {
  useCreatePaymentForInvoiceMutation,
  useInvoiceQuery,
} from './invoice.generated';
import {
  Button,
  InvoiceStatus,
  calculateInvoiceFees,
  fDate,
  fToCurrency,
  useParameters,
} from '@web/utils';
import mollieLogo from '@web/utils/src/assets/icons/logos/mollie.png';
import { InvoiceStatuses } from '@web/utils/src/components/InvoiceStatus/InvoiceStatus';
import { useCustomToast } from '@/hooks/useCustomToast';

type InvoiceRouteParams = {
  retailerId: string;
  invoiceId: string;
};

const SingleInvoice: React.FC = () => {
  const { retailerId, invoiceId } = useParameters<InvoiceRouteParams>();
  const toast = useCustomToast();
  const { t } = useTranslation();
  const [{ data }] = useInvoiceQuery({ variables: { retailerId, invoiceId } });
  const invoice = data?.retailer.invoice;
  const [_, createPaymentInvoice] = useCreatePaymentForInvoiceMutation();

  const handleCreateInvoicePayment = async (
    invoiceId: string,
    paymentOptionKey: string,
  ) => {
    try {
      const response = await createPaymentInvoice({
        invoiceId: invoiceId,
        paymentOptionKey: paymentOptionKey,
      });

      if (response.data?.createPaymentForInvoice?.checkoutUrl) {
        window.open(
          response.data?.createPaymentForInvoice?.checkoutUrl,
          '_blank',
        );
      } else {
        toast('error', t('invoices.error_creating_invoice'));
      }
    } catch (error) {
      console.log(error);
      toast('error', t('invoices.error_creating_invoice'));
    }
  };

  if (!invoice) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-x-4-0">
      <div id="order-left" className="text-body1">
        <InvoiceStatus status={invoice.status as InvoiceStatuses} t={t} />
        <InvoiceDocuments documents={[]} />
        <div className="w-full my-3-0 text-body1 p-1-6 rounded-8-0 border-2 border-secondary-grey shadow-sm font-li">
          <div className="flex justify-between">
            <span>{t('invoices.invoice_id')}</span>
            <span>ID: {invoice.id}</span>
          </div>
          <div className="flex justify-between">
            <span>{t('invoices.date_created')}</span>
            <span>{fDate(invoice.createdAt)}</span>
          </div>

          <div className="flex justify-between">
            <span>{t('invoices.total_gross')}</span>
            <span>{fToCurrency(invoice.totalGross || 0)}</span>
          </div>
        </div>
        {invoice.status !== 'PAID' && invoice.paymentOptions.length > 0 && (
          <>
            <div>
              <div className="flex justify-between items-center mb-1-0">
                <p className="font-bd">{t('invoices.buy_now_pay_later')}</p>
                <img
                  src={mollieLogo}
                  alt="mollie-logo"
                  className="w-auto h-6"
                />
              </div>
              <div className="w-full my-1-0 text-body1 p-1-6 rounded-8-0 bg-secondary-grey shadow-sm font-li">
                <ul className="list-disc px-1-6">
                  <li>
                    {t('invoices.pay_in', {
                      value: '120',
                    })}
                  </li>
                  <li>
                    {t('invoices.pay_additional', {
                      value: calculateInvoiceFees(
                        invoice.totalGross || 0,
                        invoice.paymentOptions[0].fee,
                      ),
                    })}
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <Button
                className="w-full"
                onClick={() =>
                  handleCreateInvoicePayment(
                    invoice.id,
                    invoice.paymentOptions[0].id,
                  )
                }
              >
                {t('invoices.pay_later')}
              </Button>
            </div>
          </>
        )}
      </div>
      <div>
        <InvoiceItemsTable invoice={invoice} />
      </div>
    </div>
  );
};

export default SingleInvoice;
